// btw-front/src/components/sections/ContactCTA.jsx
import React, { useMemo, useState } from "react";
import Container from "../ui/Container";
import { Button } from "../ui/Button";
import { Fade } from "react-awesome-reveal";
import { useTranslation } from "react-i18next";

const INQUIRY_TYPES = [
  { value: "CertBear", label: "CertBear" },
  { value: "TMAX", label: "TMAX" },
  { value: "OpenSource", label: "OpenSource" },
  { value: "PureStorage", label: "PureStorage" },
  { value: "ElasticSearch", label: "ElasticSearch" },
];

// 숫자만 남기고 000-0000-0000 형태로 포맷 (최대 11자리)
function formatPhone(input) {
  const digits = (input || "").replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export default function ContactCTA() {
  const { t } = useTranslation();

  const [payload, setPayload] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    inquiryType: "CertBear",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState({ type: "", message: "" }); // success | error
  const mailto = useMemo(() => {
    const subject = encodeURIComponent(`[문의] ${payload.inquiryType} / ${payload.name || "고객"}`);
    const body = encodeURIComponent(
      `회사: ${payload.company || "-"}\n` +
        `이름: ${payload.name}\n` +
        `이메일: ${payload.email || "-"}\n` +
        `연락처: ${payload.phone || "-"}\n` +
        `문의유형: ${payload.inquiryType}\n\n` +
        `${payload.message || ""}`,
    );
    return `mailto:support@btwsoft.com?subject=${subject}&body=${body}`;
  }, [payload]);

  // 이메일 필수 — 백엔드 POST /api/inquiry 도 email 필수(누락 시 400). 익명 문의 미허용.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const canSubmit = payload.name.trim() && payload.email.trim() && payload.message.trim();

  const submitInquiry = async (e) => {
    e.preventDefault();
    setResult({ type: "", message: "" });

    if (!canSubmit) {
      setResult({ type: "error", message: t("section.contact.validationRequired") });
      return;
    }
    if (!EMAIL_RE.test(payload.email.trim())) {
      setResult({ type: "error", message: t("section.contact.validationEmail") });
      return;
    }

    setIsSubmitting(true);
    try {
      // 백엔드 req.body 필드명: name, email, content (+ phone, company, product 저장/알림)
      // 폼의 '문의유형' 선택지는 실제로는 제품 목록(CertBear·TMAX 등) — product 로 전송해
      // inquiry.product 컬럼에 저장 (기존엔 inquiryType 으로 보내 product 가 NULL 로 유실됨)
      const body = {
        company: payload.company.trim(), // 선택
        name: payload.name.trim(), // 필수
        email: payload.email.trim(), // 필수
        phone: payload.phone.trim(), // 선택
        product: payload.inquiryType, // 문의 제품
        content: payload.message.trim(), // 필수
      };

      // const endpoint = `${apiBase}/api/inquiry`;
      const endpoint = `/api/inquiry`;

      // 전송 타임아웃 15초 — AbortSignal.timeout() 은 Safari 16 / Chrome 103 미만에서 미지원이라
      // browserslist 운영 범위 호환을 위해 AbortController + setTimeout 으로 구현
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      let res;
      try {
        res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || t("section.contact.submitFailed"));
      }

      setResult({
        type: "success",
        message: t("section.contact.submitted"),
      });

      setPayload({
        company: "",
        name: "",
        email: "",
        phone: "",
        inquiryType: "CertBear",
        message: "",
      });
    } catch (err) {
      // 타임아웃/중단(AbortController abort)은 영문 브라우저 메시지라 노출하지 않고 공통 실패 문구 사용
      const isAborted = err?.name === "TimeoutError" || err?.name === "AbortError";
      const msg = (!isAborted && err?.message) || t("section.contact.submitFailed");
      setResult({ type: "error", message: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="btw-section bg-slate-100">
      <Container>
        <Fade direction="up" triggerOnce>
          <div className="btw-card p-6 md:p-10">
            <div className="grid gap-10 md:grid-cols-2 md:items-start">
              <div>
                <span className="btw-eyebrow">Contact</span>
                <h2 className="btw-h2 mt-4">{t("section.contactTitle")}</h2>
                <p className="btw-lead">{t("section.contactDesc")}</p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    variant="accent"
                    onClick={() => {
                      const form = document.getElementById("contact-cta-form");
                      form?.requestSubmit();
                    }}
                  >
                    {t("section.contact.submit")}
                  </Button>

                  <Button variant="ghost" href={mailto}>
                    {t("section.contact.mailto")}
                  </Button>
                </div>
              </div>

              <form
                id="contact-cta-form"
                className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
                onSubmit={submitInquiry}
              >
                <div className="grid gap-3">
                  {/* 회사(선택) */}
                  <Field
                    label={t("section.contact.fields.company")}
                    value={payload.company}
                    onChange={(v) => setPayload((p) => ({ ...p, company: v }))}
                    placeholder={t("section.contact.fields.companyPlaceholder")}
                  />

                  {/* 이름(필수) */}
                  <Field
                    label={t("section.contact.fields.name")}
                    value={payload.name}
                    onChange={(v) => setPayload((p) => ({ ...p, name: v }))}
                  />

                  {/* 문의유형 select */}
                  <SelectField
                    label={t("section.contact.fields.inquiryType")}
                    value={payload.inquiryType}
                    onChange={(v) => setPayload((p) => ({ ...p, inquiryType: v }))}
                    options={INQUIRY_TYPES}
                  />

                  {/* 연락처: 숫자만 입력 + 자동 포맷 */}
                  <Field
                    label={t("section.contact.fields.phone")}
                    value={payload.phone}
                    onChange={(v) => setPayload((p) => ({ ...p, phone: formatPhone(v) }))}
                    inputMode="numeric"
                    placeholder={t("section.contact.fields.phonePlaceholder")}
                    maxLength={13}
                  />

                  {/* 이메일(필수) — 회신 수단 */}
                  <Field
                    label={t("section.contact.fields.email")}
                    type="email"
                    value={payload.email}
                    onChange={(v) => setPayload((p) => ({ ...p, email: v }))}
                    placeholder={t("section.contact.fields.emailPlaceholder")}
                  />

                  {/* 문의내용(필수) */}
                  <Field
                    label={t("section.contact.fields.message")}
                    type="textarea"
                    value={payload.message}
                    onChange={(v) => setPayload((p) => ({ ...p, message: v }))}
                    placeholder={t("section.contact.fields.messagePlaceholder")}
                  />

                  {result.message ? (
                    <div
                      className={`rounded-xl px-4 py-3 text-sm ${
                        result.type === "success"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : "bg-rose-50 text-rose-700 border border-rose-100"
                      }`}
                    >
                      {result.message}
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className={`w-full h-11 rounded-xl text-sm font-medium ${
                      !canSubmit || isSubmitting
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-black text-white hover:opacity-90"
                    }`}
                  >
                    {isSubmitting ? t("section.contact.submitting") : t("section.contact.submit")}
                  </button>

                  <div className="text-xs text-slate-500">{t("section.contact.note")}</div>
                </div>
              </form>
            </div>
          </div>
        </Fade>
      </Container>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, inputMode, placeholder, maxLength }) {
  return (
    <label className="grid gap-2">
      <div className="text-xs font-medium text-slate-700">{label}</div>
      {type === "textarea" ? (
        <textarea
          className="min-h-[120px] rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-400"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || label}
        />
      ) : (
        <input
          className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-slate-400"
          value={value}
          type={type}
          inputMode={inputMode}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || label}
          maxLength={maxLength}
        />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="grid gap-2">
      <div className="text-xs font-medium text-slate-700">{label}</div>
      <select
        className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-slate-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
